<template>
  <sp-fieldset :legend="label" class="context-menu__fieldset --column-settings" flex>
    <sp-container flex direction="column">
      <sp-label for="column-width">Column Width</sp-label>
      <InputRangeSlider v-model="cols" min="1" max="12" step="1" auto show-ticks value-and-unit-width="3ch" />
    </sp-container>

    <sp-container flex direction="column">
      <sp-label for="column-width">Column Mobile Width</sp-label>
      <InputRangeSlider v-model="colsSm" min="1" max="12" step="1" auto show-ticks value-and-unit-width="3ch" />
    </sp-container>

    <sp-container flex direction="column">
      <sp-label for="label">Label</sp-label>
      <sp-text-field v-model="label"></sp-text-field>
    </sp-container>

    <sp-container flex direction="column">
      <sp-label for="label">Static Content ID</sp-label>
      <!-- `.attr` sets through the attribute channel; a bare `:value` is re-applied over the pick. -->
      <sp-select
        :value.attr="column.static_content_id"
        :items="staticContentOptions"
        appearance="inline"
        @input="updateStaticContentId"
      ></sp-select>
    </sp-container>

    <sp-container flex direction="column">
      <sp-switch :checked="column.foreground" label="Foreground" @update-checked="updateForeground"></sp-switch>
    </sp-container>
  </sp-fieldset>
</template>

<script setup>
import { watchDeep } from "@vueuse/core";
import { computed, ref } from "vue";
import { AUTO_VALUE } from "#ce/utils/css";
import InputRangeSlider from "#ce/components/SpLayoutBuilder/components/LayoutBuilderContextMenu/InputRangeSlider.ce.vue";

const emit = defineEmits(["update:modelValue"]);

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
});

const column = ref(props.modelValue);
watchDeep(
  () => props.modelValue,
  (newVal) => (column.value = newVal),
  { immediate: true },
);
watchDeep(column, (newVal) => emit("update:modelValue", newVal));

const cols = computed({
  get: () => column.value.cols ?? AUTO_VALUE,
  set: (value) => (column.value.cols = value === AUTO_VALUE ? undefined : value),
});

const colsSm = computed({
  get: () => column.value.colsSm ?? AUTO_VALUE,
  set: (value) => (column.value.colsSm = value === AUTO_VALUE ? undefined : value),
});

const label = computed({
  get: () => column.value.label ?? `Column ${props.index + 1}`,
  set: (value) => (column.value.label = value),
});

const staticContentOptions = computed(() => {
  return [
    { title: "None", value: null },
    { title: "Broadcast Messages", value: "broadcast_messages" },
    { title: "Breadcrumbs", value: "breadcrumbs" },
    { title: "Header Content", value: "header_content" },
    { title: "Before Content", value: "before_content" },
    { title: "Byline", value: "byline" },
    { title: "Before Sidebar", value: "before_content_sidebar" },
    { title: "Future Bar", value: "future_bar" },
    { title: "Anchor Bar", value: "anchor_bar" },
    { title: "Anchor Bar Content", value: "anchor_bar_content" },
    { title: "Description", value: "description_content" },
    { title: "Sidebar", value: "sidebar" },
    { title: "Related From Search", value: "related_from_search" },
    { title: "Main Content", value: "main_content" },
    { title: "After Content", value: "after_content" },
  ];
});

function updateStaticContentId({ detail }) {
  const [value] = detail;
  column.value.static_content_id = value;
}

function updateForeground({ detail }) {
  const [value] = detail;
  column.value.foreground = value;
}
</script>

<style lang="scss" scoped>
sp-fieldset::part(legend) {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 16px;
  position: absolute;
  top: -14px;
  background-color: #fff;
  padding: 4px 6px;
  border-radius: 3px;
  left: 24px;
  border: 1px solid var(--sp-comp-context-menu-fieldset-border-color, #ccc);
  font-size: var(--sp-ref-font-size-x-small, 0.75rem);
  text-transform: uppercase;
  font-weight: var(--sp-comp-context-menu-fieldset-legend-font-weight, 700);
  color: var(--sp-comp-context-menu-fieldset-legend-color, #bbb);
}
</style>
